import { useState, useEffect, useMemo } from "react";
import "../style/MenuStock.css";
import CarOverview from "./carOverviewSubasta";
import api from "../utils/apiClient";
import { Spin } from "antd";

// Client-side filter for the in-place auction search. `filter` is null (show
// all) or { matches: [{type,value}], query }. Typed matches (marca/modelo/anio/
// lote/ubicacion) are ANDed across types and ORed within a type; with no typed
// match we fall back to a raw substring over the car's title fields.
function auctionMatchesFilter(item, filter) {
  if (!filter) return true;
  const car = item.Carro;
  if (!car) return false;

  const { matches, query } = filter;
  if (matches && matches.length > 0) {
    const fieldByType = {
      marca: car.marca,
      modelo: car.modelo,
      anio: car.anio,
      lote: car.lote,
      ubicacion: car.ubicacion,
    };
    const valuesByType = {};
    for (const m of matches) {
      (valuesByType[m.type] = valuesByType[m.type] || []).push(
        String(m.value).toLowerCase()
      );
    }
    return Object.keys(valuesByType).every((type) => {
      // anio (a DB Number) / lote / ubicacion are exact identifiers — compare
      // exactly so a search for "123456" can't match lote "1234567".
      // marca/modelo allow a substring so a canonical brand/model still
      // matches compound names (e.g. modelo "Tacoma" matching "Tacoma TRD").
      const field = String(fieldByType[type] ?? "").toLowerCase();
      const exact = type === "anio" || type === "lote" || type === "ubicacion";
      return valuesByType[type].some((v) =>
        exact ? field === v : field === v || field.includes(v)
      );
    });
  }

  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const hay = `${car.marca || ""} ${car.modelo || ""} ${car.anio || ""} ${
    car.lote || ""
  }`.toLowerCase();
  return hay.includes(q);
}

// The auctions listing: one `auctions/` fetch (the endpoint returns the full
// non-hidden set — no pagination), bucketed client-side into live / upcoming /
// ended, filtered in place by the SearchBar's parsed matches. This component
// started as a copy of the stock page's MenuStock; the pagination machinery
// (findspecial fetchers, scroll loader, URL-param filter path) fetched STOCK
// CARS, not auctions, and has been removed.
function AuctionListing({ filter }) {
  const [carros, setCarros] = useState([]);
  const [loading, setLoading] = useState(true);

  // Drives the live/upcoming/ended grouping below. Ticking it every 30s lets a
  // card migrate from "Próximas" to "En vivo" when its start time passes,
  // without a refetch.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        const response = await api.get("auctions/");
        setCarros(response.data);
      } catch (error) {
        console.error("Failed to fetch auctions", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAuctions();
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Quiet re-sync every 60s: a soft-close extension rewrites an auction's Fin
  // but the bidPlaced broadcast only reaches that auction's socket room — the
  // listing would otherwise show "Concluida" on the stale Fin while the
  // auction is genuinely live in its extended window. Also picks up newly
  // created auctions. Failures keep the last good list.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const response = await api.get("auctions/");
        setCarros(response.data);
      } catch (error) {
        console.error("Failed to refresh auctions", error);
      }
    }, 60000);
    return () => clearInterval(id);
  }, []);

  // Bucket auctions into live / upcoming / ended so the listing can show them
  // as separate sections (live first). Within each: live ends-soonest first,
  // upcoming starts-soonest first, ended most-recently-ended first.
  const sections = useMemo(() => {
    const live = [];
    const upcoming = [];
    const ended = [];
    for (const item of carros) {
      // Skip auctions whose populated Carro was deleted — the card renders
      // nothing for them, so counting them would inflate the section count.
      if (!item || !item.Carro) continue;
      if (!auctionMatchesFilter(item, filter)) continue;
      const start = new Date(item.Inicio).getTime();
      const end = item.Fin ? new Date(item.Fin).getTime() : null;
      if (end !== null && now >= end) ended.push(item);
      else if (now >= start) live.push(item);
      else upcoming.push(item);
    }
    live.sort((a, b) => new Date(a.Fin) - new Date(b.Fin));
    upcoming.sort((a, b) => new Date(a.Inicio) - new Date(b.Inicio));
    ended.sort((a, b) => new Date(b.Fin) - new Date(a.Fin));
    return [
      { key: "live", mod: "live", label: "Subastas en vivo", items: live },
      { key: "upcoming", mod: "soon", label: "Próximas subastas", items: upcoming },
      { key: "ended", mod: "ended", label: "Finalizadas", items: ended },
    ];
  }, [carros, now, filter]);

  // Gate the empty-state on actually-renderable auctions, not the raw response:
  // auctions whose Carro was deleted are filtered out above, so a non-empty
  // `carros` can still yield zero cards — show the message instead of a blank.
  const hasVisibleAuctions = sections.some((section) => section.items.length > 0);

  return (
    <div className="stock-container">
      <div className="carbox">
        {loading ? (
          <div className="loading-overlay-stock">
            <Spin size="large" />
          </div>
        ) : hasVisibleAuctions ? (
          sections.map((section) =>
            section.items.length === 0 ? null : (
              <section className="auction-section" key={section.key}>
                <h2
                  className={`auction-section-title auction-section-title--${section.mod}`}
                >
                  {section.mod === "live" && (
                    <span className="auction-section-dot" />
                  )}
                  <span className="auction-section-label">{section.label}</span>
                  <span className="auction-section-count">
                    {section.items.length}
                  </span>
                </h2>
                <div className="auction-grid">
                  {section.items.map((carro) => (
                    <CarOverview key={carro._id} carro={carro} />
                  ))}
                </div>
              </section>
            )
          )
        ) : (
          <div className="no-cars-message">
            {filter
              ? "No hay subastas que coincidan con tu búsqueda."
              : "No hay subastas disponibles en este momento."}
          </div>
        )}
      </div>
    </div>
  );
}

export default AuctionListing;
